import { AxiosError } from 'axios'
import api from '@/api/axiosApi'

export type PowerBiDashboardType = 'CAT' | 'IFP'

export type ExportFormat = 'PDF' | 'PPTX' | 'PNG'

export interface PowerBiExportResult {
  blob: Blob
  filename: string
  contentType: string
}

const EXPORT_FORMAT_EXTENSIONS: Record<ExportFormat, string> = {
  PDF: 'pdf',
  PPTX: 'pptx',
  PNG: 'png',
}

function parseFilenameFromContentDisposition(
  header: string | undefined,
  fallback: string
): string {
  if (!header) return fallback
  const utf8Match = /filename\*\s*=\s*UTF-8''([^;]+)/i.exec(header)
  if (utf8Match) {
    try {
      return decodeURIComponent(utf8Match[1].trim())
    } catch {
      // fall through to ascii match
    }
  }
  const asciiMatch = /filename\s*=\s*"?([^";]+)"?/i.exec(header)
  if (asciiMatch) return asciiMatch[1].trim()
  return fallback
}

async function readProblemFromBlob(blob: Blob): Promise<string | null> {
  try {
    const text = await blob.text()
    if (!text) return null
    const parsed = JSON.parse(text) as { detail?: string; title?: string }
    return parsed.detail ?? parsed.title ?? null
  } catch {
    return null
  }
}

export interface PowerBiEmbedConfig {
  reportId: string
  embedUrl: string
  accessToken: string
  expiry: string
}

interface EmbedConfigResponse {
  report_id: string
  embed_url: string
  access_token: string
  expiry: string
}

export async function fetchPowerBiEmbedConfig(
  reportId: string,
  reportType: PowerBiDashboardType
): Promise<PowerBiEmbedConfig> {
  const { data } = await api.post<EmbedConfigResponse>(
    '/dashboards/embed-config',
    {
      report_id: reportId,
      report_type: reportType,
    }
  )

  return {
    reportId: data.report_id,
    embedUrl: data.embed_url,
    accessToken: data.access_token,
    expiry: data.expiry,
  }
}

export async function exportPowerBiReport(
  reportId: string,
  reportType: PowerBiDashboardType,
  format: ExportFormat,
  bookmarkState?: string,
  pageName?: string
): Promise<PowerBiExportResult> {
  try {
    const response = await api.post<Blob>(
      `/dashboards/${encodeURIComponent(reportId)}/export`,
      {
        format,
        report_type: reportType,
        ...(bookmarkState ? { bookmark_state: bookmarkState } : {}),
        ...(pageName ? { page_name: pageName } : {}),
      },
      { responseType: 'blob' }
    )

    const contentType =
      (response.headers['content-type'] as string | undefined) ??
      'application/octet-stream'
    const filename = parseFilenameFromContentDisposition(
      response.headers['content-disposition'] as string | undefined,
      `report.${EXPORT_FORMAT_EXTENSIONS[format]}`
    )

    return { blob: response.data, filename, contentType }
  } catch (err) {
    if (err instanceof AxiosError && err.response?.data instanceof Blob) {
      const message = await readProblemFromBlob(err.response.data)
      if (message) throw new Error(message)
    }
    throw err
  }
}
